import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, MenuItem, Alert, CircularProgress, Grid, Tabs, Tab
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import ModuleHub from '../components/ModuleHub';
import { getBankAccounts, createBankAccount, getBankTransactions, createBankTransaction } from '../../../services/api';

const BankAccountsPage = () => {
  const [tab, setTab] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accDialog, setAccDialog] = useState(false);
  const [txnDialog, setTxnDialog] = useState(false);
  const [accForm, setAccForm] = useState({ account_name: '', bank_name: '', account_number: '', account_type: 'checking', opening_balance: 0 });
  const [txnForm, setTxnForm] = useState({ bank_account_id: '', transaction_date: new Date().toISOString().split('T')[0], transaction_type: 'deposit', amount: 0, description: '' });

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (tab === 0) { const r = await getBankAccounts(); setAccounts(r.data || []); }
      else { const r = await getBankTransactions(); setTransactions(r.data || []); }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const saveAccount = async () => {
    await createBankAccount({ ...accForm, current_balance: accForm.opening_balance });
    setAccDialog(false);
    loadData();
  };

  const saveTransaction = async () => {
    await createBankTransaction({ ...txnForm, bank_account_id: parseInt(txnForm.bank_account_id), amount: parseFloat(txnForm.amount) });
    setTxnDialog(false);
    loadData();
  };

  return (
    <ModuleHub moduleId="bank-cash" showFeatures={false}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Bank Accounts" />
        <Tab label="Transactions" />
      </Tabs>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        {tab === 0 ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAccDialog(true)}>Add Account</Button>
          : <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTxnDialog(true)}>Add Transaction</Button>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : tab === 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow><TableCell>Account</TableCell><TableCell>Bank</TableCell><TableCell>Type</TableCell><TableCell align="right">Balance</TableCell></TableRow></TableHead>
            <TableBody>
              {accounts.map(a => (
                <TableRow key={a.id}>
                  <TableCell><strong>{a.account_name}</strong><br /><small>{a.account_number}</small></TableCell>
                  <TableCell>{a.bank_name}</TableCell>
                  <TableCell><Chip label={a.account_type} size="small" /></TableCell>
                  <TableCell align="right">{parseFloat(a.current_balance).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Account</TableCell><TableCell>Type</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
            <TableBody>
              {transactions.map(t => (
                <TableRow key={t.id}>
                  <TableCell>{t.transaction_date}</TableCell>
                  <TableCell>{t.bankAccount?.account_name}</TableCell>
                  <TableCell><Chip label={t.transaction_type} size="small" color={t.transaction_type === 'deposit' ? 'success' : 'error'} /></TableCell>
                  <TableCell>{t.description}</TableCell>
                  <TableCell align="right">{parseFloat(t.amount).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={accDialog} onClose={() => setAccDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Bank Account</DialogTitle>
        <DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}><TextField fullWidth label="Account Name" value={accForm.account_name} onChange={(e) => setAccForm({ ...accForm, account_name: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Bank Name" value={accForm.bank_name} onChange={(e) => setAccForm({ ...accForm, bank_name: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Account Number" value={accForm.account_number} onChange={(e) => setAccForm({ ...accForm, account_number: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth select label="Type" value={accForm.account_type} onChange={(e) => setAccForm({ ...accForm, account_type: e.target.value })}>{['checking', 'savings', 'credit', 'petty_cash'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
          <Grid item xs={6}><TextField fullWidth type="number" label="Opening Balance" value={accForm.opening_balance} onChange={(e) => setAccForm({ ...accForm, opening_balance: e.target.value })} /></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setAccDialog(false)}>Cancel</Button><Button variant="contained" onClick={saveAccount}>Save</Button></DialogActions>
      </Dialog>
      <Dialog open={txnDialog} onClose={() => setTxnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Transaction</DialogTitle>
        <DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}><TextField fullWidth select label="Bank Account" value={txnForm.bank_account_id} onChange={(e) => setTxnForm({ ...txnForm, bank_account_id: e.target.value })}>{accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.account_name}</MenuItem>)}</TextField></Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} value={txnForm.transaction_date} onChange={(e) => setTxnForm({ ...txnForm, transaction_date: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth select label="Type" value={txnForm.transaction_type} onChange={(e) => setTxnForm({ ...txnForm, transaction_type: e.target.value })}>{['deposit', 'withdrawal', 'transfer', 'fee', 'interest'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12}><TextField fullWidth type="number" label="Amount" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Description" value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} /></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setTxnDialog(false)}>Cancel</Button><Button variant="contained" onClick={saveTransaction}>Save</Button></DialogActions>
      </Dialog>
    </ModuleHub>
  );
};

export default BankAccountsPage;
